import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    useTheme
} from '@mui/material';
import useMediaRecorder from '@wmik/use-media-recorder';
import React, { useEffect, useState } from 'react';
import ApiClient from '../../config/ApiClient';
import { useAppContext } from '../../context/AppContext';

interface LessonPlanRecorderProps {
    lessonPlanId: number;
    onRecordingChange: (isRecording: boolean) => void;
}

const LessonPlanRecorder: React.FC<LessonPlanRecorderProps> = ({
    lessonPlanId,
    onRecordingChange
}) => {
    const theme = useTheme();
    const { membership } = useAppContext();
    const availableRoles = membership?.roles || [];
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [recordingTime, setRecordingTime] = useState(0);

    const {
        status,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        mediaBlob,
        clearMediaBlob,
        error: recorderError
    } = useMediaRecorder({
        mediaStreamConstraints: {
            audio: true,
            video: false
        },
        recordScreen: false,
        onStop: async (blob: Blob) => {
            await uploadRecording(blob);
        }
    });

    // Update parent component when recording status changes
    useEffect(() => {
        const isRecording = status === 'recording' || status === 'paused';
        onRecordingChange(isRecording);
    }, [status, onRecordingChange]);

    // Track recording time
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        if (status === 'recording') {
            interval = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } else {
            setRecordingTime(0);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [status]);

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const uploadRecording = async (blob: Blob) => {
        setUploading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('recording', blob, `recording-${Date.now()}.webm`);
            formData.append('lesson_plan', lessonPlanId.toString());

            const response = await ApiClient.post('/api/lesson-recordings/upload_recording/', formData, {
                'Content-Type': 'multipart/form-data'
            });

            if (response.success) {
                clearMediaBlob();
                setError(null);
            } else {
                setError(response.error || 'Failed to upload recording');
            }
        } catch (err: any) {
            setError(err?.message || 'Failed to upload recording');
        } finally {
            setUploading(false);
        }
    };

    // Only show for coaches
    if (!availableRoles.includes('coach')) {
        return null;
    }

    const isRecording = status === 'recording';
    const isPaused = status === 'paused';
    const isStopped = status === 'stopped';

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {/* Recording Indicator */}
                {(isRecording || isPaused) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FiberManualRecordIcon
                            sx={{
                                color: isRecording ? 'error.main' : 'text.secondary',
                                animation: isRecording ? 'pulse 2s infinite' : 'none',
                                '@keyframes pulse': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.5 }
                                }
                            }}
                        />
                        <Typography variant="body2" color={isRecording ? 'error.main' : 'text.secondary'}>
                            {formatTime(recordingTime)}
                        </Typography>
                    </Box>
                )}

                {/* Control Buttons */}
                {!isRecording && !isPaused && (
                    <Button
                        onClick={() => {
                            startRecording().catch((err) => {
                                setError(err?.message || 'Failed to start recording');
                            });
                        }}
                        variant="outlined"
                        size="small"
                        startIcon={<PlayArrowIcon />}
                        disabled={uploading}
                    >
                        Start Recording
                    </Button>
                )}

                {isRecording && (
                    <>
                        <Button
                            onClick={() => pauseRecording()}
                            variant="outlined"
                            size="small"
                            startIcon={<PauseIcon />}
                        >
                            Pause
                        </Button>
                        <Button
                            onClick={() => stopRecording()}
                            variant="outlined"
                            size="small"
                            startIcon={<StopIcon />}
                            color="error"
                        >
                            Stop
                        </Button>
                    </>
                )}

                {isPaused && (
                    <>
                        <Button
                            onClick={() => resumeRecording()}
                            startIcon={<PlayArrowIcon />}
                        >
                            Resume
                        </Button>
                        <Button
                            onClick={() => stopRecording()}
                            startIcon={<StopIcon />}
                            color="error"
                        >
                            Stop
                        </Button>
                    </>
                )}

                {uploading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            Uploading...
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Error Message */}
            {(error || recorderError) && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {error || (recorderError?.message || String(recorderError))}
                </Typography>
            )}

            {/* Playback (for testing) */}
            {mediaBlob && isStopped && !uploading && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Preview:
                    </Typography>
                    <audio src={URL.createObjectURL(mediaBlob)} controls style={{ width: '100%' }} />
                </Box>
            )}
        </Box>
    );
};

export default LessonPlanRecorder;

